import React, { useState } from 'react';
import LoadingUI from '../LoadingUi';
import StatusCard, { CSS, BrandLogo, IconLock, IconShield, IconEye, IconAlert } from './StatusCard';

// The message shown while /stream/access is still in flight. Exported so App.js
// seeds its state from the same constant the "unavailable" card tests against —
// as two separate literals they drifted the moment either was reworded, and the
// card started offering the placeholder as its explanation.
export const INITIAL_AUTH_MESSAGE = 'Authenticating Details..';

const LoadingOverlay = ({
  branding = {},
  loading,
  authMessage,
  showEnterPassword,
  enteredPassword,
  setEnteredPassword,
  handlePasswordSubmit,
  passwordError,
  showSsoLogin,
  startSsoLogin,
  status,
  lockedOut = false,
  ssoError = '',
  unavailable = null,
}) => {
  const [reveal, setReveal] = useState(false);


  const hasBanner = branding.loadingBannerUrl && branding.loadingBannerUrl !== 'NOBANNER';
  const bannerIsVideo = hasBanner && branding.loadingBannerUrl.endsWith('.mp4');
  const bannerIsImage = hasBanner && !bannerIsVideo;

  return (
    <div
      style={{
        backgroundImage: bannerIsImage ? `url(${branding.loadingBannerUrl})` : 'none',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        backgroundSize: bannerIsImage ? 'cover' : 'auto',
        backgroundColor: '#111315',
        width: '100vw', height: '100vh', position: 'fixed', inset: 0,
        zIndex: 1000000, overflow: 'hidden',
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: CSS }} />

      <BrandLogo branding={branding} />

      {bannerIsVideo && (
        <video
          src={branding.loadingBannerUrl}
          autoPlay muted loop playsInline
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
        />
      )}

      {/* This overlay paints an OPAQUE full-screen background at z-index 1000000
          and sits above everything else in #videoElement — including the
          `{!fetchDone && <LoadingUI/>}` fallback App.js renders as a sibling — so
          whatever it declines to draw, the viewer simply does not see. Every
          state is therefore handled here; it used to draw nothing unless status
          was 'active', which left a failed /stream/access, and any project
          switched off, showing a blank dark screen with the real message
          painted underneath it. */}
      {/* `unavailable` wins over everything. App.js sets it the moment the
          bootstrap stops — project switched off, no published build, bad link,
          ticket refused — and clears the gates at the same time, so the viewer
          gets exactly one screen with the actual reason on it rather than a gate
          they cannot pass or a spinner that never resolves. */}
      {unavailable ? (
        <StatusCard
          branding={branding}
          banner={hasBanner}
          role="alert"
          icon={<IconAlert size={20} />}
          title={unavailable.title}
          subtitle={unavailable.message}
        />
      ) : status === 'active' ? (
        <>
          {loading && <LoadingUI message={authMessage} type={branding.loadingType} />}

          {showEnterPassword && (
            <StatusCard
              branding={branding}
              banner={hasBanner}
              label="Password required"
              icon={<IconLock />}
              title="This stream is protected"
              subtitle="Enter the password to continue."
            >
              <form className="spg-form" onSubmit={handlePasswordSubmit}>
                <div className="spg-field">
                  <input
                    className="spg-input"
                    type={reveal ? 'text' : 'password'}
                    placeholder="Password"
                    autoFocus
                    autoComplete="current-password"
                    aria-label="Password"
                    disabled={lockedOut}
                    value={enteredPassword}
                    onChange={(e) => setEnteredPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="spg-reveal"
                    onClick={() => setReveal((v) => !v)}
                    aria-label={reveal ? 'Hide password' : 'Show password'}
                  >
                    <IconEye off={reveal} />
                  </button>
                </div>

                {passwordError && (
                  <p className="spg-err" role="alert"><IconAlert />{passwordError}</p>
                )}

                <button type="submit" className="spg-btn" disabled={!enteredPassword || lockedOut}>
                  Continue
                </button>
              </form>
            </StatusCard>
          )}

          {showSsoLogin && (
            <StatusCard
              branding={branding}
              banner={hasBanner}
              label="Sign in required"
              icon={<IconShield />}
              title="Sign in to watch"
              subtitle="This stream requires you to sign in with your organization account."
            >
              <div className="spg-form">
                {ssoError && (
                  <p className="spg-err" role="alert"><IconAlert />{ssoError}</p>
                )}
                <button type="button" onClick={startSsoLogin} className="spg-btn">
                  <span className="spg-btn-row">Sign in</span>
                </button>
              </div>
            </StatusCard>
          )}
        </>
      ) : (
        // Still bootstrapping: /stream/access has not answered yet. Carry the
        // project's own loading treatment rather than a bare screen.
        <LoadingUI message={authMessage} type={branding.loadingType} />
      )}
    </div>
  );
};

export default LoadingOverlay;
